import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { MdEmojiEvents, MdUnfoldMore } from 'react-icons/md';
import { Table, Toggle, Tooltip, Whisper } from 'rsuite';
import { mapMonitoramentoDetalheToMonitoringItem } from '../../mappers';
import { getMonitoramentoDetalhe } from '../../services';
import { ActionPopover } from './ActionPopover';
import { ModalDetalhamento } from './ModalDetalhamento';
import styles from './MonitoringTable.module.scss';
import { MOCK_MONITORING_DATA } from './mockData';
import { MonitoringItem, MonitoringRange, MonitoringTableProps } from './types';

const { Column, HeaderCell, Cell, Pagination } = Table;

const formatCompetitivenessIndex = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded.toString().replace('.', ',')}%`;
};

const pluralize = (count: number, singular: string, plural: string): string => (Number(count) === 1 ? singular : plural);

const formatRecorteGeografico = (details: MonitoringItem['details']): string => {
  if (details.tipoLocal === 'Todo o território') return 'em Todo o território';

  const unit = details.tipoLocal === 'UF' ? pluralize(details.citiesCount, 'UF', 'UFs') : pluralize(details.citiesCount, 'cidade', 'cidades');
  return `em ${details.citiesCount} ${unit}`;
};

const getRangeLabel = (range: MonitoringRange): string | null => {
  switch (range.tipo) {
    case 'MAIOR_IGUAL':
      return typeof range.valorMax === 'number' ? `≥ ${range.valorMax}%` : null;
    case 'MENOR_IGUAL':
      return typeof range.valorMin === 'number' ? `≤ ${range.valorMin}%` : null;
    case 'ENTRE':
      return typeof range.valorMin === 'number' && typeof range.valorMax === 'number' ? `ENTRE ${range.valorMin}% E ${range.valorMax}%` : null;
    default:
      return null;
  }
};

const satisfiesRange = (index: number, range: MonitoringRange): boolean => {
  switch (range.tipo) {
    case 'ENTRE': {
      const temFaixaValida = typeof range.valorMin === 'number' && typeof range.valorMax === 'number';
      return !temFaixaValida || index < range.valorMin! || index > range.valorMax!;
    }
    case 'MAIOR_IGUAL':
      return typeof range.valorMax === 'number' && index <= range.valorMax;
    case 'MENOR_IGUAL':
      return typeof range.valorMin === 'number' && index >= range.valorMin;
    default:
      return false;
  }
};

const MonitoringRangeResult = ({ ranges, competitivenessIndex }: { ranges?: MonitoringRange[]; competitivenessIndex?: number | null }) => {
  const rangeLabels = (ranges ?? []).map(range => getRangeLabel(range)).filter((label): label is string => label !== null);
  const rangeBadges = rangeLabels.length > 0 && (
    <div className={styles.rangeBadgesRow}>
      {rangeLabels.map(label => (
        <Whisper key={label} trigger="hover" placement="top" speaker={<Tooltip>Regra definida para o monitoramento</Tooltip>}>
          <span className={styles.rangeBadge}>{label}</span>
        </Whisper>
      ))}
    </div>
  );

  if (competitivenessIndex === undefined || competitivenessIndex === null) {
    return (
      <>
        {rangeBadges}
        <span className={styles.indexNeutral}>Sem competitividade apurada</span>
      </>
    );
  }

  const withinRange = rangeLabels.length > 0 ? (ranges ?? []).every(range => satisfiesRange(competitivenessIndex, range)) : undefined;
  const indexColorClass = withinRange === undefined ? styles.indexNeutral : withinRange ? styles.indexWithinRange : styles.indexOutsideRange;

  return (
    <>
      {rangeBadges}
      <div className={styles.percentageRow}>
        <MdEmojiEvents size={20} className={indexColorClass} />
        <span className={indexColorClass}>{formatCompetitivenessIndex(competitivenessIndex)}</span>
      </div>
    </>
  );
};

export const MonitoringTable = ({
  data = MOCK_MONITORING_DATA,
  isLoading = false,
  page = 0,
  size = 20,
  totalElements,
  pendingToggleIds = new Set(),
  onChangePage,
  onChangeSize,
  onToggleActive,
  onOpenAnalysis,
  onEdit,
  onDuplicate,
  onDelete,
  onRemoveForMe,
}: MonitoringTableProps) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  const [tableRemountKey, setTableRemountKey] = useState(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setTableRemountKey(key => key + 1), 300);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const { data: selectedItemDetalhe } = useQuery({
    queryKey: ['monitoramento-detalhe', selectedItemId],
    queryFn: () => getMonitoramentoDetalhe(selectedItemId as number),
    enabled: selectedItemId !== null,
  });

  const selectedItem = selectedItemDetalhe ? mapMonitoramentoDetalheToMonitoringItem(selectedItemDetalhe.data) : null;

  const handleChangePage = (nextPage: number) => {
    onChangePage?.(nextPage);
  };

  const handleChangeLength = (length: number) => {
    onChangeSize?.(length);
  };

  const handleOpenDetails = (item: MonitoringItem) => {
    setSelectedItemId(item.id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItemId(null);
  };

  return (
    <div className={styles.container}>
      <Table
        key={tableRemountKey}
        data={data}
        autoHeight
        headerHeight={40}
        rowHeight={130}
        loading={isLoading}
        className={styles.table}
        rowClassName={(rowData?: MonitoringItem) => (rowData && !rowData.ativo ? styles.inactiveRow : '')}
        renderEmpty={() => (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateTitle}>Você não possui alertas criados no momento.</p>
            <p className={styles.emptyStateSubtitle}>Para criar um, clique no botão acima.</p>
          </div>
        )}
      >
        {/* @ts-ignore */}
        <Column width={70} align="center">
          <HeaderCell className={styles.headerCell} />
          <Cell>
            {(rowData: MonitoringItem) => (
              <Toggle size="sm" checked={rowData.ativo} disabled={pendingToggleIds.has(rowData.id)} onChange={checked => onToggleActive?.(rowData, checked)} />
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column flexGrow={1} minWidth={180}>
          <HeaderCell className={styles.headerCell}>Nome do monitoramento</HeaderCell>
          <Cell dataKey="name">
            {(rowData: MonitoringItem) => (
              <div>
                <div className={styles.nameCell}>{rowData.name}</div>
                {rowData.compartilhado && <p className={styles.sharedBySubtitle}>Compartilhado por {rowData.compartilhadoPor}</p>}
              </div>
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column flexGrow={1} minWidth={150}>
          <HeaderCell className={styles.headerCell}>Tipo de monitoramento</HeaderCell>
          <Cell dataKey="type">{(rowData: MonitoringItem) => <div className={styles.typeCell}>{rowData.type}</div>}</Cell>
        </Column>

        {/* @ts-ignore */}
        <Column flexGrow={2} minWidth={280}>
          <HeaderCell className={styles.headerCell}>Detalhamento</HeaderCell>
          <Cell dataKey="details">
            {(rowData: MonitoringItem) => (
              <div className={styles.detailsCell}>
                {rowData.isVarejo ? (
                  <>
                    <p className={styles.productName}>
                      Monitorando <strong>{rowData.details.redeReferenciaNome ?? '-'}</strong>
                    </p>
                    <p className={styles.detailsInfo}>
                      Vs <strong>{rowData.details.networksCount}</strong> {pluralize(rowData.details.networksCount, 'rede concorrente', 'redes concorrentes')}
                    </p>
                    <p className={styles.detailsInfo}>{formatRecorteGeografico(rowData.details)}</p>
                  </>
                ) : (
                  <>
                    <p className={styles.productName}>
                      Monitorando <strong>{rowData.details.productName}</strong>
                    </p>
                    <p className={styles.detailsInfo}>
                      Vs <strong>{rowData.details.competitorsCount}</strong>{' '}
                      {pluralize(rowData.details.competitorsCount, 'produto concorrente', 'produtos concorrentes')}
                    </p>
                    <p className={styles.detailsInfo}>
                      em <strong>{rowData.details.networksCount}</strong> {pluralize(rowData.details.networksCount, 'rede', 'redes')}{' '}
                      {formatRecorteGeografico(rowData.details)}
                    </p>
                  </>
                )}
                <p className={styles.detailsInfo}>
                  nas <strong>{rowData.details.period}</strong>
                </p>
                <button type="button" className={styles.linkButton} onClick={() => handleOpenDetails(rowData)}>
                  Visualizar
                </button>
              </div>
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column flexGrow={2} minWidth={250}>
          <HeaderCell className={styles.headerCell}>
            <div className={styles.sortableHeader}>
              Resultado do monitoramento
              <MdUnfoldMore size={16} className={styles.sortIcon} />
            </div>
          </HeaderCell>
          <Cell dataKey="ranges">
            {(rowData: MonitoringItem) => (
              <div className={styles.resultCell}>
                {(rowData.ranges && rowData.ranges.length > 0) || rowData.competitivenessIndex !== undefined ? (
                  <MonitoringRangeResult ranges={rowData.ranges} competitivenessIndex={rowData.competitivenessIndex} />
                ) : (
                  '-'
                )}
              </div>
            )}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={116} align="left">
          <HeaderCell className={styles.headerCell} />
          <Cell className={styles.actionsCellWrapper}>
            {(rowData: MonitoringItem) => (
              <div className={styles.actionsCell}>
                <button type="button" className={styles.actionLink} onClick={() => onOpenAnalysis?.(rowData)}>
                  Abrir análise
                </button>
                {!rowData.compartilhado && (
                  <button type="button" className={styles.actionLink} onClick={() => onEdit?.(rowData)}>
                    Editar
                  </button>
                )}
                <ActionPopover
                  item={rowData}
                  onOpenAnalysis={onOpenAnalysis}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onToggleActive={onToggleActive}
                  onDelete={onDelete}
                  onRemoveForMe={onRemoveForMe}
                  triggerClassName={styles.actionLink}
                />
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      <div className={styles.paginationContainer}>
        <Pagination
          lengthMenu={[
            { value: 20, label: 20 },
            { value: 50, label: 50 },
            { value: 100, label: 100 },
          ]}
          activePage={page + 1}
          displayLength={size}
          total={totalElements ?? 0}
          onChangePage={handleChangePage}
          onChangeLength={handleChangeLength}
        />
      </div>

      <ModalDetalhamento show={showModal} onClose={handleCloseModal} item={selectedItem} />
    </div>
  );
};
